<template>
  <LandingBackground>
    <div class="mb-20">
      <div
        class="mx-auto mt-10 columns-1 gap-10 leading-6 [column-fill:balance] sm:px-5 md:px-8 xl:columns-2 3xl:columns-3"
      >
        <LandingCard variant="info" :title="`Welcome, ${userRef.firstName} ${userRef.lastName}`">
          <LandingCardLinkSections label="Stationary Source Permitting, Compliance, and Source Testing">
            <LandingCardLinkSection
              link-label="Apply for a New Stationary Source Permit"
              :to="{ name: routeNames.electronicForm.stationarySourcePermittingSelect }"
            />
            <LandingCardLinkSection
              link-label="Submit a Stationary Source Compliance Form"
              :to="{ name: routeNames.electronicForm.complianceSelect }"
            />
            <LandingCardLinkSection
              link-label="Submit a Source Testing Form"
              :to="{ name: routeNames.electronicForm.sourceTestingSelect }"
            />
            <LandingCardLinkSection
              link-label="Request Access to a Stationary Source"
              description="If you need to revise, transfer, or renew an existing stationary source permit or you need to submit compliance or source testing forms, request access to your stationary source."
              :to="{ name: routeNames.accessRequest.stationarySource }"
            />
          </LandingCardLinkSections>

          <LandingCardLinkSections label="Dust Control Permitting">
            <!-- :to="{ name: routeNames.electronicForm.dustControlPermittingSelect }" -->
            <LandingCardLinkSection
              link-label="Apply for a New Dust Control Permit"
              :to="{ name: routeNames.electronicForm.dustControlPermittingSelect }"
            />
            <LandingCardLinkSection
              link-label="Request Access to an Existing Dust Control Operating Permit"
              description="Request access if you need to revise, transfer, renew, or close an existing dust control operating permit."
              :to="{ name: routeNames.accessRequest.constructionProject }"
            />
          </LandingCardLinkSections>

          <LandingCardLinkSections label="Dust Classes and Dust Monitor Classes">
            <LandingCardLinkSection
              link-label="Register for a Dust Class"
              :to="{ name: routeNames.electronicForm.dustClassSelect }"
            />
            <LandingCardLinkSection
              link-label="Register for a Dust Monitor Class"
              :to="{ name: routeNames.electronicForm.dustMonitorClassSelect }"
            />
          </LandingCardLinkSections>

          <LandingCardLinkSections label="Enforcement">
            <LandingCardLinkSection
              link-label="Request Hearing with the Air Pollution Control Hearing Board"
              :to="{ name: routeNames.electronicForm.ef040.new }"
            />
            <LandingCardLinkSection
              link-label="Appeal Hearing Officer Decision"
              :to="{ name: routeNames.electronicForm.ef039.new }"
            />
            <LandingCardLinkSection
              link-label="Apply for an Olive Tree Exemption "
              :to="{ name: routeNames.electronicForm.ef038.new }"
            />
          </LandingCardLinkSections>

          <LandingCardLinkSections label="Asbestos">
            <LandingCardLinkSection
              link-label="Submit a Notification of Asbestos Abatement"
              :to="{ name: routeNames.electronicForm.ef041.new }"
            />
            <LandingCardLinkSection
              link-label="Submit a Demolition Notification"
              :to="{ name: routeNames.electronicForm.ef042.new }"
            />
          </LandingCardLinkSections>

          <LandingCardLinkSections label="Open Burning">
            <LandingCardLinkSection
              link-label="Submit Open Burning Application and Notification"
              :to="{ name: routeNames.electronicForm.openBurningSelect }"
            />
          </LandingCardLinkSections>

          <LandingCardLinkSections label="Questions">
            <p class="text-sm leading-normal text-neutral-600">
              Contact Tanuja Puvvula at
              <a class="text-sky-600 hover:underline" href="tel:+1-775-687-9467">(702) 273-8428</a> or
              <a class="text-sky-600 hover:underline" href="mailto:tanuja@clarkcountynv.gov">tanuja@clarkcountynv.gov</a
              >.
            </p>
          </LandingCardLinkSections>
        </LandingCard>

        <!-- Questions -->
        <!-- <LandingCard>
          <template #default>
            <div class="mx-auto max-w-xl">
              <div class="px-4 pb-7 pt-6">
                <div class="text-center">
                  <h2 class="text-lg font-medium text-neutral-900">Questions?</h2>
                  <p class="mt-1 text-sm text-neutral-500">
                    <span class="text-neutral-700"></span><br />
                    <span class="leading-loose">
                      <EnvelopeIcon class="mr-2 inline-block h-4 w-4" /><a href="mailto:Tanuja@clarkcountynv.gov"
                        >Tanuja@clarkcountynv.gov</a
                      >
                    </span>
                    <br />
                    <PhoneIcon class="mr-2 inline-block h-4 w-4" />
                    <a href="tel:+1-775-687-9467">+1 (702) 273-8428</a>
                  </p>
                </div>
              </div>
            </div>
          </template>
        </LandingCard> -->

        <HomeExternalInvitations />

        <LandingCard title="Electronic Forms" :view-all-to="{ name: routeNames.electronicForm.list }">
          <div
            v-if="isGetElectronicFormsSuccess && electronicForms?._items?.length === 0"
            class="bg-neutral-50 p-10 sm:rounded-b-lg"
          >
            <NoneFoundFlag label="No Open Forms" />
          </div>
          <LandingCardSkeleton v-if="isGetElectronicFormsPending" />
          <LandingCardEntityList
            v-for="section in electronicFormSections"
            :key="section.title"
            :title="section.title"
            :items="section.items"
            :variant="section.variant"
          >
            <template #default="{ item }">
              <LandingCardEntityListItem>
                <template #primaryLabel>
                  <LookupDescription :murn="item.formMurn" />
                  &ndash;
                  <LookupDescription :murn="item.typeMurn" />
                </template>
                <template #secondaryLabel>
                  <LookupDescription :murn="item.groupMurn" />
                </template>
                <template #additionalInfo>
                  <div
                    class="mt-1.5 text-sm text-neutral-400 md:mt-0 md:self-center md:pr-4 md:text-right"
                    v-if="item.updatedDateTime && item.updatedBy"
                  >
                    <span class="md:block"
                      >Updated
                      <span class="text-neutral-500">{{ tzDate.shortDateTimeString(item.updatedDateTime) }}</span></span
                    >
                    by <span class="text-neutral-500">{{ item.updatedBy.name }}</span>
                  </div>
                </template>
              </LandingCardEntityListItem>
            </template>
          </LandingCardEntityList>
        </LandingCard>

        <HomeExternalCardsAndClasses />

        <LandingCard title="Stationary Sources" :view-all-to="{ name: routeNames.stationarySource.list }">
          <!-- <div
            v-if="isGetStationarySourcesSuccess && stationarySources?._items?.length === 0"
            class="bg-neutral-50 p-10 sm:rounded-b-lg"
          >
            <NoneFoundFlag />
          </div> -->
          <LandingCardSkeleton v-if="isGetStationarySourcesPending" />
          <LandingCardEntityList :items="stationarySources?._items">
            <!-- title="Active Permits"  -->
            <template #default="{ item }">
              <LandingCardEntityListItem :secondaryLabel="formatAddress(item)">
                <template #primaryLabel> {{ item.name }} &ndash; {{ item.number }} </template>
              </LandingCardEntityListItem>
            </template>
          </LandingCardEntityList>
          <div class="border-t border-neutral-200 px-4 py-5 sm:px-6">
            <LandingCardLinkSection
              link-label="Request Access to a Stationary Source"
              description="If you need to revise, transfer, or renew an existing stationary source permit or you need to submit compliance or source testing forms, request access to your stationary source."
              :to="{ name: routeNames.accessRequest.stationarySource }"
            />
          </div>
        </LandingCard>

        <LandingCard title="Construction Projects" :view-all-to="{ name: routeNames.constructionProject.list }">
          <!-- <div
            v-if="isGetConstructionProjectsSuccess && constructionProjects?._items?.length === 0"
            class="bg-neutral-50 p-10 sm:rounded-b-lg"
          >
            <NoneFoundFlag />
          </div> -->
          <LandingCardSkeleton v-if="isGetConstructionProjectsPending" />
          <LandingCardEntityList :items="constructionProjects?._items">
            <template #default="{ item }">
              <LandingCardEntityListItem :secondaryLabel="item.locationDescription">
                <template #primaryLabel> {{ item.name }} &ndash; {{ item.number }} </template>
              </LandingCardEntityListItem>
            </template>
          </LandingCardEntityList>
          <div class="border-t border-neutral-200 px-4 py-5 sm:px-6">
            <LandingCardLinkSection
              link-label="Request Access to a Construction Project"
              description="Request access if you need to revise, transfer, renew, or close an existing construction project."
              :to="{ name: routeNames.accessRequest.constructionProject }"
            />
          </div>
        </LandingCard>

        <!-- TODO: Add resources -->
        <!-- <LandingCard title="Resources">
          <LandingCardLinkSections label="Temporary Form Access">
            <LandingCardLinkSection
              link-label="Forms Development"
              description="Access developed form front-end."
              :to="{ name: routeNames.temporaryFormLanding }"
            />
          </LandingCardLinkSections>
        </LandingCard> -->
      </div>
    </div>
  </LandingBackground>
</template>

<script lang="ts">
import HomeExternalCardsAndClasses from "@/components/HomeExternalCardsAndClasses.vue";
import HomeExternalInvitations from "@/components/HomeExternalInvitations.vue";
import type { LandingCardEntity, LandingCardVariant } from "@/components/landing/landingCard";
import LandingCard from "@/components/landing/LandingCard.vue";
import LandingCardEntityList from "@/components/landing/LandingCardEntityList.vue";
import LandingCardEntityListItem from "@/components/landing/LandingCardEntityListItem.vue";
import LandingCardLinkSection from "@/components/landing/LandingCardLinkSection.vue";
import LandingCardLinkSections from "@/components/landing/LandingCardLinkSections.vue";
import LandingCardSkeleton from "@/components/landing/LandingCardSkeleton.vue";
import LandingBackground from "@/components/LandingBackground.vue";
import { LE } from "@/constants/enums";
import { getElectronicFormRouteNameFromItem } from "@/helpers/electronicFormHelpers";
import { routeNames } from "@/routes";
import { getConstructionProjects, getElectronicForms, getStationarySources } from "@/services/endpoints";
import type { ConstructionProject } from "@/views/constructionProject/constructionProject";
import constructionProjectService from "@/views/constructionProject/constructionProjectService";
import type { ElectronicForm, ElectronicFormBE } from "@/views/electronicForm/electronicForm";
import electronicFormService from "@/views/electronicForm/electronicFormService";
import type { StationarySource } from "@/views/stationarySource/stationarySource";
import stationarySourceService from "@/views/stationarySource/stationarySourceService";
import { EnvelopeIcon, PhoneIcon } from "@heroicons/vue/20/solid";
import { ChevronRightIcon } from "@heroicons/vue/24/outline";
import { InitialBadge } from "@modernary/mui/components/badge";
import { BaseButton } from "@modernary/mui/components/button";
import { MdrnAttribution, NoneFoundFlag, useAppSettingsStore } from "@modernary/mui/components/common";
import { adaptPagedResponse, LookupDescription } from "@modernary/mui/components/lookup";
import type { Paging } from "@modernary/mui/utils/api";
import { useApi } from "@modernary/mui/utils/api";
import { useUser } from "@modernary/mui/utils/authentication";
import { LoginButton, RegisterButton } from "@modernary/mui/utils/authentication/buttons";
import { formatAddress, tzDate } from "@modernary/mui/utils/filters";
import { computed, defineComponent, watch } from "vue";

interface ElectronicFormSection {
  title: string;
  variant?: LandingCardVariant;
  items: LandingCardEntity<ElectronicForm>[] | undefined;
}

// Home content for unauth'd users
export default defineComponent({
  name: "HomeExternal",
  components: {
    LandingBackground,
    BaseButton,
    InitialBadge,
    LoginButton,
    RegisterButton,
    ChevronRightIcon,
    MdrnAttribution,
    NoneFoundFlag,
    PhoneIcon,
    EnvelopeIcon,
    LandingCardLinkSection,
    LandingCardLinkSections,
    LandingCardEntityList,
    LandingCardEntityListItem,
    LandingCardSkeleton,
    LandingCard,
    HomeExternalCardsAndClasses,
    HomeExternalInvitations,
    LookupDescription,
  },
  setup() {
    const { userRef } = useUser();
    const appSettingsStore = useAppSettingsStore();

    const isAuthenticated = computed(() => userRef.value.isAuthenticated);

    /* 
      Landing pages have a lot of unconventional code,
      not a wise place to use as a reference.
    */

    const {
      dataRef: constructionProjects,
      exec: execGetConstructionProjects,
      isStatusPending: isGetConstructionProjectsPending,
      isStatusSuccess: isGetConstructionProjectsSuccess,
    } = useApi(getConstructionProjects, {
      responseAdapter: async (response) => {
        const data = await adaptPagedResponse(response, constructionProjectService.skinnyResponseAdapter);
        data._items.forEach((item: any) => {
          item.to = { name: routeNames.constructionProject.view, params: { id: item.id } };
        });

        return data as Paging<LandingCardEntity<ConstructionProject>>;
      },
    });

    const {
      dataRef: stationarySources,
      exec: execGetStationarySources,
      isStatusPending: isGetStationarySourcesPending,
      isStatusSuccess: isGetStationarySourcesSuccess,
    } = useApi(getStationarySources, {
      responseAdapter: async (response) => {
        const data = await adaptPagedResponse(response, stationarySourceService.responseAdapter);
        data._items.forEach((item: any) => {
          item.to = { name: routeNames.stationarySource.view, params: { id: item.id } };
        });
        return data as Paging<LandingCardEntity<StationarySource>>;
      },
    });

    const {
      dataRef: electronicForms,
      exec: execGetElectronicForms,
      isStatusPending: isGetElectronicFormsPending,
      isStatusSuccess: isGetElectronicFormsSuccess,
    } = useApi(getElectronicForms, {
      responseAdapter: async (response) => {
        const data = (await adaptPagedResponse(
          response,
          electronicFormService.responseAdapter as any,
        )) as unknown as Paging<LandingCardEntity<ElectronicForm>>;
        data._items.forEach((item) => {
          item.to = { name: getElectronicFormRouteNameFromItem(item), params: { id: item.id } };
        });
        return data as Paging<LandingCardEntity<ElectronicForm>>;
      },
    });

    watch(
      isAuthenticated,
      (newValue) => {
        if (newValue) {
          execGetStationarySources();
          execGetConstructionProjects();
          execGetElectronicForms({
            statusMurnIn: [
              LE.ElectronicFormStatus.Draft,
              LE.ElectronicFormStatus.SubmissionPending,
              LE.ElectronicFormStatus.SubmissionPaymentPending,
              LE.ElectronicFormStatus.PaymentPending,
              LE.ElectronicFormStatus.AwaitingScheduling,
            ],
            isParent: true, //Exclude worksheets
            orderBy: [{ propertyName: "updatedDateTime", isDescending: true }],
            take: 7, // TODO: remove this once users are bound to forms
          });
        }
      },
      { immediate: true },
    );

    // Group forms by status. Sections with no forms render nothing.
    const formsWithStatus = (statusMurn: LE.ElectronicFormStatus) =>
      electronicForms.value?._items.filter((item) => item.statusMurn === statusMurn);

    const electronicFormSections = computed<ElectronicFormSection[]>(() => [
      {
        title: "Payment Pending",
        variant: "warn",
        items: formsWithStatus(LE.ElectronicFormStatus.PaymentPending),
      },
      {
        title: "Submission Payment Pending",
        variant: "warn",
        items: formsWithStatus(LE.ElectronicFormStatus.SubmissionPaymentPending),
      },
      { title: "Submission Pending", items: formsWithStatus(LE.ElectronicFormStatus.SubmissionPending) },
      {
        title: "Awaiting Scheduling",
        variant: "info",
        items: formsWithStatus(LE.ElectronicFormStatus.AwaitingScheduling),
      },
      { title: "Draft", items: formsWithStatus(LE.ElectronicFormStatus.Draft), variant: "plain" },
    ]);

    return {
      electronicFormSections,
      userRef,
      BaseButton,
      appSettingsStore,
      isGetConstructionProjectsPending,
      isGetConstructionProjectsSuccess,
      constructionProjects,
      isGetStationarySourcesPending,
      isGetStationarySourcesSuccess,
      stationarySources,
      routeNames,
      isGetElectronicFormsPending,
      isGetElectronicFormsSuccess,
      electronicForms,
      formatAddress,
      tzDate,
    };
  },
});
</script>
